import {
  TagDetailPopoverContent,
  TagDetailPopoverTrigger,
} from "@/components/mapping/retrieval-results/tag-detail-popover";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/styles/ui/popover";
import { TagType } from "@/types/mapping";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Sigma, Sparkle } from "lucide-react";
import { useRef, useState } from "react";

const SearchResultItem = ({
  id,
  isStandard,
  isSum,
  isSelected,
  label,
  prefix,
  name,
  tagType,
  handleClick,
  className,
  innerClassName,
  shownContentWhenSelected,
  contentContainerClassName,
  description,
  isSameTag,
}: {
  id: string;
  isStandard: boolean;
  isSum: boolean;
  isSelected: boolean;
  label: string;
  prefix: string;
  name: string;
  tagType: TagType;
  handleClick: () => Promise<void>;
  className?: string;
  innerClassName?: string;
  shownContentWhenSelected?: React.ReactNode;
  contentContainerClassName?: string;
  description?: string | null;
  isSameTag?: boolean;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const anchorPositionRef = useRef<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  return (
    <Popover
      onOpenChange={(open) => {
        if (open && anchorRef.current) {
          anchorPositionRef.current.top =
            anchorRef.current.getBoundingClientRect().top;
          anchorPositionRef.current.left =
            anchorRef.current.getBoundingClientRect().left;
        }
        setIsPopoverOpen(open);
      }}
    >
      <div
        className={cn(
          "relative flex flex-col items-start self-stretch",
          className,
        )}
      >
        <PopoverAnchor
          className={cn(
            !isPopoverOpen && "popover-anchor absolute -left-[16px]",
            isPopoverOpen && "popover-anchor fixed",
          )}
          style={
            isPopoverOpen
              ? {
                  top: anchorPositionRef.current.top,
                  left: anchorPositionRef.current.left,
                }
              : undefined
          }
          ref={anchorRef}
        />
        {/* TagListItem */}
        <div
          className={cn(
            "group flex h-[36px] py-[8px] px-[12px] flex-row items-center self-stretch rounded-[8px] bg-white hover:bg-neutral-100 cursor-pointer active:bg-blue-100",
            !isStandard && "hover:bg-purple-100",
            innerClassName,
          )}
          onClick={async () => await handleClick()}
        >
          <div
            className={cn(
              "w-[0px] h-[0px] mr-[0px] flex p-[0px] justify-center items-center shrink-0 aspect-square rounded-full bg-[#0A0A0A] transition-all group-hover:w-[14px] group-hover:h-[14px] group-hover:mr-[6px] group-hover:p-[3px] group-hover:opacity-35",
              isSelected &&
                "w-[14px] h-[14px] mr-[6px] p-[3px] opacity-100 group-hover:opacity-100",
            )}
          >
            <Check
              className={cn(
                "w-[0px] h-[0px] flex-shrink-0 aspect-square text-white transition-all group-hover:w-[8px] group-hover:h-[8px]",
                isSelected && "w-[8px] h-[8px]",
              )}
            />
          </div>
          <div
            className={cn(
              "w-[0px] h-[0px] mr-[0px] flex p-[0px] justify-center items-center shrink-0 aspect-square transition-all group-hover:w-[0px] group-hover:h-[0px] group-hover:mr-[0px] group-hover:p-[0px] group-hover:opacity-35",
              !isStandard &&
                !isSelected &&
                "w-[14px] h-[14px] mr-[6px] p-[3px] opacity-100 group-hover:opacity-100",
            )}
          >
            <Sparkle
              fill="currentColor"
              className={cn(
                "w-[0px] h-[0px] flex-shrink-0 aspect-square text-purple-600 transition-all group-hover:w-[0px] group-hover:h-[0px]",
                !isStandard && !isSelected && "w-[14px] h-[14px]",
              )}
            />
          </div>
          {isSum && (
            <div className="flex items-center mr-[6px] p-[2px] gap-[10px] rounded-[2px] bg-orange-500">
              <Sigma className="w-[8px] h-[8px] text-white" />
            </div>
          )}
          <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
            <span
              className={cn(
                "text-[#171717] label-1-normal-medium",
                !isStandard && "text-purple-600",
              )}
            >
              {label}
            </span>
          </div>
          {isSameTag && (
            <div className="bg-orange-200 text-[12px] font-[600] rounded-[4px] px-2 py-0.5 text-orange-900 caption-2-regular mx-1">
              동일태그
            </div>
          )}
          <PopoverTrigger onClick={(e) => e.stopPropagation()}>
            <TagDetailPopoverTrigger isOpen={isPopoverOpen} />
          </PopoverTrigger>
        </div>
        {/* Frame 2087329551 */}
        <AnimatePresence>
          {isSelected && (
            <motion.div
              className={cn("w-full", contentContainerClassName)}
              key={id}
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
              transition={{ duration: 0.2 }}
            >
              {shownContentWhenSelected}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <PopoverContent
        side="left"
        align="start"
        className="flex w-[300px] max-h-[600px] flex-col items-start rounded-2xl bg-white shadow-lg p-0 m-0 border-none"
      >
        <TagDetailPopoverContent
          prefix={prefix}
          name={name}
          label={label}
          type={tagType}
          description={description ?? ""}
        />
      </PopoverContent>
    </Popover>
  );
};

export default SearchResultItem;
